/**
 * 관리자 화면 — Laravel Blade(`web`) 관리자 페이지 대체.
 *
 * 레거시는 목록 화면마다 jQuery `$.ajax` 로 서로 다른 엔드포인트를 호출하고
 * `returnCode`(200/202/203/402/403)를 각자 해석했다. 여기서는 @solar/api-client
 * 한 곳에서만 통신하고, 회사 격리(멀티테넌시)는 서버가 강제한다.
 *
 * 구조는 레거시 sidebar.blade.php 를 따른 사이드바 + 콘텐츠. 라우팅 라이브러리
 * 없이 메뉴 키 하나로 화면을 고른다 — 새 메뉴는 MENUS 와 아래 분기, routes.ts 의
 * MENU_PATH 에 한 줄씩 추가. 주소(/orders/3080 …)는 상태를 비추는 거울이다: 상태가
 * 바뀌면 주소를 밀고, 뒤로가기·새로고침·딥링크는 주소에서 상태를 되살린다.
 */

import { useEffect, useRef, useState } from 'react';

import type { OrderStatusFilter } from '@solar/api-client';
import { ROLE_LABEL, navigate, useLocation, useSession } from '@solar/ui';

import { api } from './api';
import { pathToRoute, routeToPath, type MenuKey } from './routes';
import { AsDetail } from './screens/AsDetail';
import { AsTable } from './screens/AsTable';
import { BoardManage } from './screens/BoardManage';
import { CapacityManage } from './screens/CapacityManage';
import { CitizenQnaManage } from './screens/CitizenQnaManage';
import { CompanyManage } from './screens/CompanyManage';
import { Dashboard } from './screens/Dashboard';
import { FaqManage } from './screens/FaqManage';
import { InventoryEdit } from './screens/InventoryEdit';
import { InventoryManage } from './screens/InventoryManage';
import { Login } from './screens/Login';
import { OrderCreate } from './screens/OrderCreate';
import { OrderDetail } from './screens/OrderDetail';
import { OrderTable } from './screens/OrderTable';
import { ProductManage } from './screens/ProductManage';
import { StatOrderAs } from './screens/StatOrderAs';
import { StatSettlement } from './screens/StatSettlement';
import { SubsidyManage } from './screens/SubsidyManage';
import { TeamManage } from './screens/TeamManage';
import { TerminalManage } from './screens/TerminalManage';
import { UninstallTable } from './screens/UninstallTable';
import { Pdf2018 } from './screens/Pdf2018';
import { PermissionManage } from './screens/PermissionManage';
import { PwChangeUsers } from './screens/PwChangeUsers';
import { RoleHistory } from './screens/RoleHistory';
import { RoleManage } from './screens/RoleManage';
import { UserHistory } from './screens/UserHistory';
import { UserManage } from './screens/UserManage';

/**
 * 레거시 사이드바(sidebar.blade.php) 메뉴명 그대로. 그룹 제목은 클릭되지 않고
 * 하위 항목만 화면을 고른다. 항목의 `permission` 은 세션의 권한 코드(서버
 * app/core/permissions.py, /auth/me 의 permissions)로 노출 여부를 정한다 — 레거시
 * getPermissionMenu 의 '읽기' 항목과 같은 기준이다. 접근 자체는 서버가 강제한다
 * (권한 없는 계정이 열면 403 메시지가 그대로 보인다). 권한을 적지 않은 항목은
 * 항상 보인다(대시보드·FAQ·이력 화면).
 */
const MENUS: Array<{
  label: string;
  items: Array<{ key: MenuKey; label: string; permission?: string }>;
}> = [
  { label: '', items: [{ key: 'dashboard', label: '대시보드' }] },
  {
    label: '주문 관리',
    items: [
      { key: 'orders', label: '주문 목록', permission: 'order.read' },
      { key: 'pdf_2018', label: '2018년도 전자계약서 조회', permission: 'order.pdf_2018' },
    ],
  },
  {
    label: '철거 관리',
    items: [{ key: 'uninstall', label: '철거 완료 목록', permission: 'uninstall.read' }],
  },
  { label: 'AS 관리', items: [{ key: 'as', label: 'AS 목록', permission: 'as.read' }] },
  {
    label: '정보 관리',
    items: [
      { key: 'subsidy_si', label: '[시]보조금 관리', permission: 'subsidy_si.read' },
      { key: 'subsidy_gu', label: '[구]보조금 관리', permission: 'subsidy_gu.read' },
      { key: 'capacity', label: '지원 가능 용량 관리', permission: 'capacity.read' },
      { key: 'terminal', label: '단말 모델 관리', permission: 'terminal.read' },
      { key: 'product', label: '상품 관리', permission: 'product.read' },
    ],
  },
  {
    // 레거시 사이드바 순서 그대로 설치 회사 관리 앞에 둔다 (sidebar.blade.php:134-155).
    label: '재고 관리',
    items: [
      { key: 'inventory_list', label: '재고 목록', permission: 'inventory.read' },
      { key: 'inventory_edit', label: '재고 등록', permission: 'inventory.edit' },
    ],
  },
  {
    label: '설치 회사 관리',
    items: [
      { key: 'company', label: '설치 회사 정보', permission: 'company.read' },
      { key: 'team', label: '설치 팀 정보', permission: 'team.read' },
    ],
  },
  {
    // 레거시 사이드바 순서 그대로 통계 앞에 둔다 (sidebar.blade.php:181-222).
    label: '게시판',
    items: [
      { key: 'board_notice', label: '공지사항', permission: 'board_notice.read' },
      { key: 'board_data', label: '자료실', permission: 'board_data.read' },
      { key: 'board_member', label: '직원게시판', permission: 'board_member.read' },
      { key: 'board_citizen', label: '시민회원 QNA', permission: 'board_citizen.read' },
      // FAQ 는 레거시 권한 격자에도 없다 — 서버 역할 검사(board_service)만 적용.
      { key: 'board_faq', label: '자주하는 질문' },
    ],
  },
  {
    // 레거시 사이드바 순서 그대로 계정 관리 앞에 둔다 (sidebar.blade.php:226-252).
    label: '통계',
    items: [
      { key: 'stat_order', label: '주문 통계', permission: 'stat.order' },
      { key: 'stat_money', label: '정산 통계', permission: 'stat.money' },
      { key: 'stat_as', label: 'AS 통계', permission: 'stat.as' },
    ],
  },
  {
    label: '계정 관리',
    items: [
      { key: 'permission', label: 'Permission', permission: 'permission.read' },
      { key: 'role', label: 'Role', permission: 'role.read' },
      { key: 'user', label: '사용자', permission: 'user.read' },
      { key: 'user_history', label: '사용자 작업 이력' },
      { key: 'pw_change_users', label: '비밀번호 변경 조회', permission: 'user.pw_change' },
      { key: 'role_history', label: '권한 변경 이력', permission: 'user.role_history' },
    ],
  },
];

/** 메뉴 키 → 화면 이름. 모바일 헤더에 현재 위치를 보여주는 데 쓴다. */
const MENU_LABEL: Record<MenuKey, string> = Object.fromEntries(
  MENUS.flatMap((g) => g.items.map((m) => [m.key, m.label])),
) as Record<MenuKey, string>;

/**
 * 사이드바 접힘 기준 — styles.css 의 @media (max-width: 1100px)와 반드시 같아야
 * 한다. 1100px 아래에서는 콘텐츠 2단(admin__body)도 1단으로 접히므로, 내비게이션
 * 드로어 전환도 같은 폭에서 일어나야 어색한 중간 상태가 없다.
 */
const NAV_BREAKPOINT = '(max-width: 1100px)';

export function App() {
  const session = useSession(api);
  const location = useLocation();
  // 레거시 로그인 직후 랜딩(/web/dashboard)과 동일하게 대시보드가 기본 화면 — 단,
  // 딥링크/새로고침은 주소의 화면으로 바로 간다(로그인 화면을 거쳐도 주소는 남는다).
  const [initial] = useState(() => pathToRoute(window.location.pathname));
  const [menu, setMenu] = useState<MenuKey>(initial.menu);
  const [selectedOrder, setSelectedOrder] = useState<number | null>(initial.selectedOrder);
  const [selectedUninstall, setSelectedUninstall] = useState<number | null>(
    initial.selectedUninstall,
  );
  const [selectedAs, setSelectedAs] = useState<number | null>(initial.selectedAs);
  // 주문 등록 화면 여부 — 라우팅 라이브러리 없이 상태로만 화면을 고른다.
  const [creatingOrder, setCreatingOrder] = useState(initial.creatingOrder);
  // 상세에서 배정/완료/철거신청이 일어나면 목록도 다시 읽어야 한다.
  const [asVersion, setAsVersion] = useState(0);
  const [uninstallVersion, setUninstallVersion] = useState(0);
  // 대시보드 카드 → 목록 이동 시의 초기 필터. key 로 넘겨 목록을 새로 마운트한다.
  const [ordersPreset, setOrdersPreset] = useState<OrderStatusFilter | null>(null);
  const [asPreset, setAsPreset] = useState<number | null>(null);
  // 콜센터 카드(qna_pending/qna_done) → 시민 QNA 목록의 답변 여부 초기 필터.
  const [qnaPreset, setQnaPreset] = useState<boolean | null>(null);
  // 모바일 내비게이션 드로어 — 좁은 화면(NAV_BREAKPOINT)에서만 쓴다.
  const [navOpen, setNavOpen] = useState(false);
  const [isNarrow, setIsNarrow] = useState(() => window.matchMedia(NAV_BREAKPOINT).matches);
  const navRef = useRef<HTMLElement>(null);
  const navToggleRef = useRef<HTMLButtonElement>(null);

  // 주소 → 상태 (뒤로가기/앞으로가기, 주소창 입력). 같은 값이면 React 가 재렌더를 건너뛴다.
  useEffect(() => {
    const r = pathToRoute(location.path);
    setMenu(r.menu);
    setCreatingOrder(r.creatingOrder);
    setSelectedOrder(r.selectedOrder);
    setSelectedUninstall(r.selectedUninstall);
    setSelectedAs(r.selectedAs);
  }, [location.path]);

  // 상태 → 주소. 첫 실행은 모르는 경로를 정규화하는 것이라 히스토리를 남기지 않는다.
  const firstSync = useRef(true);
  useEffect(() => {
    const path = routeToPath({ menu, creatingOrder, selectedOrder, selectedUninstall, selectedAs });
    if (path !== window.location.pathname) navigate(path, { replace: firstSync.current });
    firstSync.current = false;
  }, [menu, creatingOrder, selectedOrder, selectedUninstall, selectedAs]);

  useEffect(() => {
    const handler = () => void session.logout();
    window.addEventListener('solar:unauthorized', handler);
    return () => window.removeEventListener('solar:unauthorized', handler);
  }, [session]);

  useEffect(() => {
    const mq = window.matchMedia(NAV_BREAKPOINT);
    const onChange = (e: MediaQueryListEvent) => {
      setIsNarrow(e.matches);
      // 넓어지면 사이드바가 항상 보이므로 드로어 상태는 버린다.
      if (!e.matches) setNavOpen(false);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // 드로어가 열린 동안: 배경 스크롤 잠금, ESC 로 닫기, 포커스를 드로어 안에 가둔다.
  useEffect(() => {
    if (!isNarrow || !navOpen) return;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setNavOpen(false);
        return;
      }
      if (e.key !== 'Tab') return;
      const nav = navRef.current;
      if (!nav) return;
      const items = nav.querySelectorAll<HTMLButtonElement>('button');
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;
      if (!nav.contains(active)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && (active === first || active === nav)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    // 포커스를 드로어 안으로 — 현재 화면 항목이 있으면 거기부터.
    const nav = navRef.current;
    (nav?.querySelector<HTMLButtonElement>('button.active') ?? nav)?.focus();
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
      // 닫히면 포커스를 햄버거 버튼으로 돌려놓는다.
      navToggleRef.current?.focus();
    };
  }, [isNarrow, navOpen]);

  if (session.restoring) return <p className="spinner">세션 확인 중…</p>;
  if (!session.user) return <Login onLogin={session.login} />;

  /**
   * 권한 코드 보유 여부. permissions 가 없거나 비어 있으면(권한 계층 이전 서버, 또는
   * 아직 갱신되지 않은 세션) 전부 보여준다 — 서버가 403 으로 막으므로 숨기는 건
   * 편의일 뿐이고, 잘못 숨기면 메뉴가 통째로 사라져 더 위험하다.
   */
  const permissions = session.user.permissions ?? [];
  const can = (code?: string) =>
    code === undefined || permissions.length === 0 || permissions.includes(code);
  const visibleMenus = MENUS.map((g) => ({
    ...g,
    items: g.items.filter((m) => can(m.permission)),
  })).filter((g) => g.items.length > 0);

  // 모바일 헤더에 보여줄 현재 화면 이름 — 주문 등록만 목록과 다른 화면이라 예외.
  const screenLabel = menu === 'orders' && creatingOrder ? '주문 등록' : MENU_LABEL[menu];

  return (
    <div className="admin">
      <header className="admin__head">
        <button
          ref={navToggleRef}
          type="button"
          className="nav-toggle"
          aria-label={navOpen ? '메뉴 닫기' : '메뉴 열기'}
          aria-expanded={navOpen}
          aria-controls="admin-nav"
          onClick={() => setNavOpen((v) => !v)}
        >
          ☰
        </button>
        <h1>미니태양광 관리</h1>
        {/* 좁은 화면에서 현재 위치 — 넓은 화면에서는 CSS 로 숨긴다. */}
        <span className="admin__screen">{screenLabel}</span>
        <span className="who">
          {session.user.name ?? session.user.user_id} · {ROLE_LABEL[session.user.type] ?? '-'}
        </span>
        <button type="button" className="link" onClick={() => void session.logout()}>
          로그아웃
        </button>
      </header>

      <div className="admin__frame">
        {isNarrow && navOpen && (
          <div className="nav-back" aria-hidden="true" onClick={() => setNavOpen(false)} />
        )}
        <nav
          id="admin-nav"
          ref={navRef}
          tabIndex={-1}
          aria-label="주 메뉴"
          className={navOpen ? 'admin__nav admin__nav--open' : 'admin__nav'}
          // 좁은 화면에서만 모달 드로어 — 넓은 화면에서는 평범한 사이드바다.
          {...(isNarrow && navOpen ? { role: 'dialog', 'aria-modal': true } : {})}
        >
          {visibleMenus.map((group) => (
            <div key={group.label || (group.items[0]?.key ?? '')} className="nav-group">
              {group.label !== '' && <span className="nav-group__title">{group.label}</span>}
              {group.items.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  className={menu === m.key ? 'active' : ''}
                  onClick={() => {
                    // 사이드바로 직접 들어오면 대시보드에서 온 초기 필터를 버린다.
                    setOrdersPreset(null);
                    setAsPreset(null);
                    setQnaPreset(null);
                    setMenu(m.key);
                    setNavOpen(false);
                  }}
                >
                  {m.label}
                </button>
              ))}
            </div>
          ))}
        </nav>

        {menu === 'dashboard' && (
          <Dashboard
            onNavigate={(target) => {
              if (target.menu === 'orders') {
                setOrdersPreset(target.status);
                setSelectedOrder(null);
                setCreatingOrder(false);
                setMenu('orders');
              } else if (target.menu === 'board_citizen') {
                setQnaPreset(target.responded ?? null);
                setMenu('board_citizen');
              } else {
                setAsPreset(target.state ?? null);
                setSelectedAs(null);
                setMenu('as');
              }
            }}
          />
        )}

        {menu === 'orders' && creatingOrder && (
          <div className="admin__body admin__body--single">
            <section className="admin__list">
              <OrderCreate
                role={session.user.type}
                companyId={session.user.build_company_id}
                onCancel={() => setCreatingOrder(false)}
                onCreated={(id) => {
                  // 등록 성공 → 목록으로 돌아가 해당 주문 상세를 연다.
                  // 서버가 확정한 금액·자부담을 상세에서 바로 확인한다.
                  setCreatingOrder(false);
                  setSelectedOrder(id);
                }}
              />
            </section>
          </div>
        )}

        {menu === 'orders' && !creatingOrder && (
          <div className="admin__body">
            <section className="admin__list">
              <OrderTable
                key={`orders-${ordersPreset ?? 'all'}`}
                role={session.user.type}
                selected={selectedOrder}
                onSelect={setSelectedOrder}
                onCreate={() => setCreatingOrder(true)}
                initialStatus={ordersPreset ?? 'all'}
              />
            </section>
            <aside className="admin__aside">
              {selectedOrder === null ? (
                <p className="muted">주문을 선택하세요.</p>
              ) : (
                <OrderDetail
                  orderId={selectedOrder}
                  onClose={() => setSelectedOrder(null)}
                  onChanged={() => setUninstallVersion((v) => v + 1)}
                />
              )}
            </aside>
          </div>
        )}

        {menu === 'uninstall' && (
          <div className="admin__body">
            <section className="admin__list">
              <UninstallTable
                selected={selectedUninstall}
                onSelect={setSelectedUninstall}
                version={uninstallVersion}
              />
            </section>
            <aside className="admin__aside">
              {selectedUninstall === null ? (
                <p className="muted">철거 건을 선택하세요.</p>
              ) : (
                <OrderDetail
                  orderId={selectedUninstall}
                  onClose={() => setSelectedUninstall(null)}
                  onChanged={() => setUninstallVersion((v) => v + 1)}
                />
              )}
            </aside>
          </div>
        )}

        {menu === 'as' && (
          <div className="admin__body">
            <section className="admin__list">
              <AsTable
                key={`as-${asPreset ?? 'all'}`}
                selected={selectedAs}
                onSelect={setSelectedAs}
                version={asVersion}
                initialState={asPreset ?? undefined}
              />
            </section>
            <aside className="admin__aside">
              {selectedAs === null ? (
                <p className="muted">A/S 건을 선택하세요.</p>
              ) : (
                <AsDetail
                  asId={selectedAs}
                  onClose={() => setSelectedAs(null)}
                  onChanged={() => setAsVersion((v) => v + 1)}
                />
              )}
            </aside>
          </div>
        )}

        {/* 정보 관리 — 마스터 화면은 각자 목록+상세 2단 레이아웃을 스스로 그린다. */}
        {menu === 'subsidy_si' && <SubsidyManage key="si" kind="si" />}
        {menu === 'subsidy_gu' && <SubsidyManage key="gu" kind="gu" />}
        {menu === 'capacity' && <CapacityManage />}
        {menu === 'terminal' && <TerminalManage />}
        {menu === 'product' && <ProductManage />}

        {/* 재고 관리 — 사이드바는 inventory.* 권한으로 걸렀고, 회사 격리는 서버가 강제한다. */}
        {menu === 'inventory_list' && <InventoryManage />}
        {menu === 'inventory_edit' && <InventoryEdit />}

        {/* 설치 회사 관리 */}
        {menu === 'company' && <CompanyManage />}
        {menu === 'team' && <TeamManage />}

        {/* 게시판 — 작성/삭제 권한별 버튼은 각 화면이 거르고, 서버가 403 으로 이중 방어한다. */}
        {menu === 'board_notice' && (
          <BoardManage key="board-notice" kind="notice" role={session.user.type} />
        )}
        {menu === 'board_data' && (
          <BoardManage key="board-data" kind="data" role={session.user.type} />
        )}
        {menu === 'board_member' && (
          <BoardManage key="board-member" kind="member" role={session.user.type} />
        )}
        {menu === 'board_citizen' && (
          <CitizenQnaManage
            key={`qna-${qnaPreset ?? 'all'}`}
            initialResponded={qnaPreset ?? undefined}
          />
        )}
        {menu === 'board_faq' && <FaqManage role={session.user.type} />}

        {/* 통계 — 사이드바는 stat.* 권한으로 걸렀고, 서버도 403 으로 이중 방어한다. */}
        {menu === 'stat_order' && <StatOrderAs key="stat-order" kind="order" />}
        {menu === 'stat_money' && <StatSettlement />}
        {menu === 'stat_as' && <StatOrderAs key="stat-as" kind="as" />}

        {/* 계정 관리 */}
        {menu === 'pdf_2018' && <Pdf2018 />}
        {menu === 'permission' && <PermissionManage />}
        {menu === 'role' && <RoleManage />}
        {menu === 'user' && <UserManage viewerRole={session.user.type} />}
        {menu === 'user_history' && <UserHistory viewerRole={session.user.type} />}
        {menu === 'pw_change_users' && <PwChangeUsers />}
        {menu === 'role_history' && <RoleHistory />}
      </div>
    </div>
  );
}
